<div [class]="getContainerClasses()">
  @if (mode === 'date-picker') {
    @if (label) {
      <label [class]="getLabelClasses()">
        {{ label }}
        @if (required) {
          <span class="ml-0.5 text-red-500 dark:text-red-400">*</span>
        }
      </label>
    }

    <div class="relative" appClickOutside (appClickOutside)="closeCalendar()">
      <button
        type="button"
        [class]="getDateTriggerClasses()"
        [style.border-color]="calendarOpen() && themeColor ? themeColor : null"
        [disabled]="disabled"
        (click)="toggleCalendar()"
      >
        <span [class.text-[#7d7d93]]="!displayDate()" [class.dark:text-ink-subtle]="!displayDate()">
          {{ displayDate() || datePlaceholder }}
        </span>
        <app-icon-v2 name="calendar" size="16" class="shrink-0 text-[#7d7d93] dark:text-ink-muted"></app-icon-v2>
      </button>

      @if (calendarOpen()) {
        <div
          class="absolute left-0 top-[calc(100%+4px)] z-[200] w-[280px] rounded-[12px] border border-[#e5e7eb] bg-white p-3 shadow-xl dark:border-surface-3 dark:bg-surface-1"
        >
          <div class="mb-3 flex items-center justify-between">
            <button
              type="button"
              [class]="getNavButtonClasses(canGoPrev())"
              [disabled]="!canGoPrev()"
              (click)="navigatePrev()"
            >
              <app-icon-v2 name="chevron-left" size="16"></app-icon-v2>
            </button>
            @if (isHeaderClickable()) {
              <button type="button" [class]="getHeaderButtonClasses()" (click)="onHeaderClick()">
                {{ getCalendarHeaderLabel() }}
                <app-icon-v2 name="chevron-down" size="14" class="text-[#7d7d93] dark:text-ink-muted"></app-icon-v2>
              </button>
            } @else {
              <span [class]="getHeaderButtonClasses()">
                {{ getCalendarHeaderLabel() }}
              </span>
            }
            <button
              type="button"
              [class]="getNavButtonClasses(canGoNext())"
              [disabled]="!canGoNext()"
              (click)="navigateNext()"
            >
              <app-icon-v2 name="chevron-right" size="16"></app-icon-v2>
            </button>
          </div>

          @if (calendarView() === 'days') {
            <div class="mb-1 grid grid-cols-7 gap-1">
              @for (weekDay of weekDays; track weekDay) {
                <div class="flex h-8 items-center justify-center text-[11px] font-medium text-[#7d7d93] dark:text-ink-muted">
                  {{ weekDay }}
                </div>
              }
            </div>

            <div class="grid grid-cols-7 gap-1">
              @for (day of calendarDays(); track day.date.format('YYYY-MM-DD')) {
                <button
                  type="button"
                  [class]="getCalendarDayClasses(day)"
                  [style.background-color]="day.isSelected && themeColor ? themeColor : null"
                  [style.color]="day.isToday && !day.isSelected && themeColor ? themeColor : null"
                  [disabled]="day.isDisabled"
                  (click)="selectDate(day)"
                >
                  {{ day.date.date() }}
                </button>
              }
            </div>
          } @else if (calendarView() === 'months') {
            <div class="grid grid-cols-4 gap-1">
              @for (month of monthShortNames; track month; let i = $index) {
                <button
                  type="button"
                  [class]="getPickerCellClasses(isMonthDisabled(i), isCurrentMonthSelected(i))"
                  [style.background-color]="isCurrentMonthSelected(i) && themeColor ? themeColor : null"
                  [disabled]="isMonthDisabled(i)"
                  (click)="selectMonth(i)"
                >
                  {{ month }}
                </button>
              }
            </div>
          } @else {
            <div class="grid grid-cols-4 gap-1">
              @for (year of yearGridYears(); track year) {
                <button
                  type="button"
                  [class]="getPickerCellClasses(isYearDisabled(year), isCurrentYearSelected(year))"
                  [style.background-color]="isCurrentYearSelected(year) && themeColor ? themeColor : null"
                  [disabled]="isYearDisabled(year)"
                  (click)="selectYear(year)"
                >
                  {{ year }}
                </button>
              }
            </div>
          }
        </div>
      }
    </div>
  } @else {
    @if (label) {
      <label [class]="getLabelClasses()">
        {{ label }}
        @if (required) {
          <span class="ml-0.5 text-red-500 dark:text-red-400">*</span>
        }
      </label>
    }

    <div [class]="getOptionsWrapperClasses()">
      @for (option of options; track option.value) {
        <label [class]="getOptionClasses(option)" (click)="$event.preventDefault(); onOptionChange(option)">
          <input
            [type]="mode === 'single' ? 'radio' : 'checkbox'"
            [name]="name"
            [value]="option.value"
            [checked]="isSelected(option)"
            [disabled]="option.disabled || disabled"
            [required]="required"
            [class]="getInputClasses()"
          />

          <div class="flex items-center gap-3">
            @if (variant !== 'button') {
              <div [class]="getCustomInputClasses(isSelected(option))">
                @if (mode === 'single') {
                  <div [class]="getCustomInputInnerClasses(isSelected(option))"></div>
                } @else {
                  @if (isSelected(option)) {
                    <svg class="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                  }
                }
              </div>
            }

            <div class="flex-1">
              <span [class]="getLabelTextClasses(option)">{{ option.label }}</span>
              @if (option.description) {
                <p [class]="getDescriptionClasses()">{{ option.description }}</p>
              }
            </div>
          </div>
        </label>
      }
    </div>
  }

  @if (hint && !error) {
    <p [class]="getHintClasses()">{{ hint }}</p>
  }

  @if (error) {
    <p [class]="getErrorClasses()">
      <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fill-rule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clip-rule="evenodd"
        ></path>
      </svg>
      {{ error }}
    </p>
  }
</div>
